import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bug, AlertTriangle, Trash2, Copy, RefreshCw, Clock, Filter, Server, Laptop } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

export default function ErrorLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | backend | frontend
  const [expanded, setExpanded] = useState(null); // id of expanded row

  const fetchAll = async () => {
    setLoading(true);
    try {
      const source = filter === 'all' ? '' : `?source=${filter}`;
      const [logsRes, summaryRes] = await Promise.all([
        api.get(`/admin/error-logs${source}`),
        api.get('/admin/error-logs/summary'),
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch {
      // swallow
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const copyToClipboard = (log) => {
    const text = `[${log.source}] ${log.method} ${log.route} — ${log.status}
User: ${log.user_name || 'anonymous'} (${log.user_id || '-'})
Time: ${log.created_at}
Message: ${log.message}

Stack Trace:
${log.stack_trace}

User Agent: ${log.user_agent || '-'}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Stack trace copied — paste into Emergent chat'),
      () => toast.error('Failed to copy')
    );
  };

  const deleteLog = async (id) => {
    try {
      await api.delete(`/admin/error-logs/${id}`);
      setLogs((prev) => prev.filter((l) => l.id !== id));
      toast.success('Log deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const purgeOld = async () => {
    if (!window.confirm('Delete all error logs older than 30 days?')) return;
    try {
      const res = await api.post('/admin/error-logs/purge-old?days=30');
      toast.success(`Purged ${res.data.deleted} old logs`);
      fetchAll();
    } catch {
      toast.error('Purge failed');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Wipe ALL error logs? Cannot be undone.')) return;
    try {
      const res = await api.delete('/admin/error-logs');
      toast.success(`Cleared ${res.data.deleted} logs`);
      fetchAll();
    } catch {
      toast.error('Clear failed');
    }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="error-log-forbidden">
        <Bug className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Super Admin Only</h2>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl lg:max-w-none mx-auto space-y-4" data-testid="error-log-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Bug className="w-6 h-6 text-red-500" />
            Error Logs
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Copy any stack trace and paste into Emergent chat — I'll debug it.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchAll}
          className="rounded-full"
          data-testid="error-log-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="error-log-summary">
          <Card className="p-3 rounded-xl border">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Total</div>
            <div className="text-2xl font-bold">{summary.total}</div>
          </Card>
          <Card className={`p-3 rounded-xl border ${summary.last_24h > 0 ? 'border-red-500/40 bg-red-500/5' : ''}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Last 24h</div>
            <div className={`text-2xl font-bold ${summary.last_24h > 0 ? 'text-red-500' : ''}`}>{summary.last_24h}</div>
          </Card>
          <Card className="p-3 rounded-xl border">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1"><Server className="w-3 h-3" /> Backend</div>
            <div className="text-2xl font-bold">{summary.backend_total}</div>
          </Card>
          <Card className="p-3 rounded-xl border">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1"><Laptop className="w-3 h-3" /> Frontend</div>
            <div className="text-2xl font-bold">{summary.frontend_total}</div>
          </Card>
        </div>
      )}

      {/* Filter + bulk actions */}
      <div className="flex gap-2 items-center flex-wrap" data-testid="error-log-filters">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {['all', 'backend', 'frontend'].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)}
            className="rounded-full text-xs capitalize"
            data-testid={`error-filter-${f}`}
          >
            {f}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={purgeOld} className="rounded-full text-xs" data-testid="error-purge-btn">
          Purge &gt; 30d
        </Button>
        <Button size="sm" variant="ghost" onClick={clearAll} className="rounded-full text-xs text-red-500" data-testid="error-clear-btn">
          <Trash2 className="w-3 h-3 mr-1" /> Clear all
        </Button>
      </div>

      {/* Logs list */}
      {loading ? (
        <p className="text-center text-muted-foreground py-8">Loading...</p>
      ) : logs.length === 0 ? (
        <Card className="p-8 text-center border-2 rounded-2xl" data-testid="error-log-empty">
          <Bug className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No errors logged — all clear!</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => (
            <motion.div
              key={log.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(i * 0.015, 0.3) }}
            >
              <Card className="p-3 border rounded-xl" data-testid={`error-log-${log.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${log.source === 'backend' ? 'bg-red-500/20 text-red-600' : 'bg-orange-500/20 text-orange-600'}`}>
                    {log.source === 'backend' ? <Server className="w-4 h-4" /> : <Laptop className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-full bg-muted">{log.source}</span>
                      {log.status > 0 && (
                        <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-red-500/10 text-red-600">
                          {log.method} {log.status}
                        </span>
                      )}
                      <span className="text-xs font-mono text-muted-foreground truncate">{log.route}</span>
                    </div>
                    <p className="text-sm font-semibold mt-1 break-words">{log.message}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(log.created_at).toLocaleString()}</span>
                      {log.user_name && <span>· {log.user_name}</span>}
                    </p>

                    {expanded === log.id && log.stack_trace && (
                      <pre className="mt-2 text-xs font-mono bg-background border rounded p-2 max-h-64 overflow-auto whitespace-pre-wrap break-all" data-testid={`error-stack-${log.id}`}>
                        {log.stack_trace}
                      </pre>
                    )}

                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" onClick={() => setExpanded(expanded === log.id ? null : log.id)} className="rounded-full text-xs h-7" data-testid={`error-expand-${log.id}`}>
                        {expanded === log.id ? 'Hide' : 'View'} stack
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(log)} className="rounded-full text-xs h-7" data-testid={`error-copy-${log.id}`}>
                        <Copy className="w-3 h-3 mr-1" /> Copy for Emergent
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteLog(log.id)} className="rounded-full text-xs h-7 text-red-500 hover:text-red-600" data-testid={`error-delete-${log.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
